// src/components/LeadershipHubView.jsx

import React, { useState, useEffect, useRef } from 'react';
import { 
    FileText, AlertCircle, Activity, Shield, ShieldAlert,
    Download, CheckCircle2, X,
    Video, Link as LinkIcon, ExternalLink, MessageSquare, Sparkles,
    RefreshCw, Users, Target, Crosshair, Brain, ChevronDown, BookOpen, Clock
} from 'lucide-react';

// --- ADVANCED YOUTUBE PLAYER ---
const CustomYouTubePlayer = ({ videoId, onNext }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isEnded, setIsEnded] = useState(false);

    useEffect(() => {
        setIsEnded(false); 
        if (!videoId) return;

        const loadAPI = () => {
            return new Promise((resolve) => {
                if (window.YT && window.YT.Player) {
                    resolve(window.YT);
                    return;
                }
                
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                window.onYouTubeIframeAPIReady = () => resolve(window.YT);
            });
        };

        let player;
        loadAPI().then((YT) => {
            if (!containerRef.current) return;
            
            const el = document.createElement('div');
            el.className = "w-full h-full absolute inset-0";
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(el);

            player = new YT.Player(el, {
                videoId: videoId,
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1,
                    showinfo: 0
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                            } else if (document.webkitFullscreenElement) {
                                document.webkitExitFullscreen().catch(() => {});
                            } else if (document.mozFullScreenElement) {
                                document.mozCancelFullScreen().catch(() => {});
                            } else if (document.msFullscreenElement) {
                                document.msExitFullscreen().catch(() => {});
                            }
                            setIsEnded(true);
                        } else if (event.data === YT.PlayerState.PLAYING) {
                            setIsEnded(false);
                        }
                    }
                }
            });
            playerRef.current = player;
        });

        return () => {
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId]);

    return (
        <div className="relative w-full h-full bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            <div ref={containerRef} className="absolute inset-0 w-full h-full"></div>
            {isEnded && (
                <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center animate-in fade-in duration-300">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                        <CheckCircle2 size={40} className="text-eas-blue" />
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl font-black mb-8 text-center px-4 tracking-tight">Resource Complete</h3>
                    <button 
                        onClick={() => {
                            setIsEnded(false);
                            if (playerRef.current) playerRef.current.playVideo();
                        }} 
                        className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 border border-white/10"
                    >
                        <RefreshCw size={18} /> Replay
                    </button>
                </div>
            )}
        </div>
    );
};


// --- AI COACHING SCENARIOS ---
const COACHING_SCENARIOS = [
    { 
        title: "Practice Tough Feedback", 
        desc: "Roleplay delivering constructive criticism.",
        prompt: "I need to deliver constructive feedback to an underperforming team member. Can you roleplay as the employee so I can practice my approach? Start by acting as the defensive employee.", 
        icon: <MessageSquare className="text-pink-500"/>,
        bg: "bg-pink-50", border: "border-pink-100"
    },
    { 
        title: "Navigate Burnout", 
        desc: "Scripts for supporting exhausted team members.",
        prompt: "One of my top performers is showing signs of severe burnout and withdrawal. What specific questions should I ask them in our 1-on-1 today to help them feel supported without overstepping?", 
        icon: <Activity className="text-orange-500"/>,
        bg: "bg-orange-50", border: "border-orange-100"
    },
    { 
        title: "Resolve Conflict", 
        desc: "Mediation frameworks for internal disputes.",
        prompt: "Two of my team members are having a personality clash that is affecting the wider group's morale. Give me a step-by-step mediation framework to address this in a joint meeting.", 
        icon: <ShieldAlert className="text-red-500"/>,
        bg: "bg-red-50", border: "border-red-100"
    },
    { 
        title: "Build Trust Fast", 
        desc: "Strategies for new leaders or new teams.",
        prompt: "I'm taking over a new team. What are the highest-ROI actions I can take in the first 30 days to build deep psychological safety and trust?", 
        icon: <Users className="text-blue-500"/>,
        bg: "bg-blue-50", border: "border-blue-100"
    }
];

// --- LEADERSHIP FRAMEWORKS ---
const FRAMEWORKS = [
    { 
        title: "SBI Feedback Model", 
        desc: "The gold standard for removing emotion and delivering objective, actionable feedback.", 
        steps: [
            { label: "Situation", text: "Be specific. 'Yesterday at the 10am client meeting...'" },
            { label: "Behavior", text: "State facts, not judgments. 'You interrupted Sarah twice...'" },
            { label: "Impact", text: "State the result. 'It caused us to lose our train of thought and look unaligned.'" }
        ] 
    },
    { 
        title: "The GROW Model", 
        desc: "A coaching framework to help your team members solve their own problems instead of relying on you.", 
        steps: [
            { label: "Goal", text: "'What exactly do you want to achieve here?'" },
            { label: "Reality", text: "'What is the current situation? What is blocking you?'" },
            { label: "Options", text: "'What are 3 different ways you could tackle this?'" },
            { label: "Will", text: "'Which option will you commit to, and by when?'" }
        ] 
    },
    { 
        title: "Radical Candor", 
        desc: "Finding the sweet spot between being a jerk and being overly empathetic.", 
        steps: [
            { label: "Care Personally", text: "Build a foundation of trust. They must know you genuinely care about their success." },
            { label: "Challenge Directly", text: "Do not sugarcoat the truth. Clear is kind. Unclear is unkind." },
            { label: "The Avoidance", text: "Beware of 'Ruinous Empathy' (caring but remaining silent to avoid hurting feelings)." }
        ] 
    }
];

// --- MASTERCLASS ARTICLES (From Real Email Content) ---
const MASTERCLASS_ARTICLES = [
    {
        id: "mc_1",
        title: "The Power of Routines",
        part: "Part 1",
        readTime: "4 min read",
        type: "Article",
        content: [
            { type: "p", text: "On October 18, 2009, Tom Brady and the New England Patriots took on the Tennessee Titans. From the very first snap of the game, Brady and the Patriots looked completely dialed in. The team's play calling, execution, and performance were near flawless. Brady completed 29 of 34 passes for 380 yards, and 6 touchdowns in an absolutely dominant 59-0 win." },
            { type: "p", text: "The goal of every company, business, and corporation is to maximize their performance and have outcomes like the Patriots accomplished that day. Fortune 500 companies like Walmart, Amazon, and ExxonMobil are obsessed with strategic initiatives, leadership development, and workplace efficiency. It's why companies like Walmart will invest over 70 billion in human resources each year." },
            { type: "p", text: "But what separates average performance from exceptional performance? Very few individuals have the genetic makeup of Usain Bolt or Michael Jordan. But the variables that produce peak human performance apply to everyone. The human brain is a highly sophisticated biological machine that has the capacity to improve, get better, and grow. A Harvard study found measurable structural changes in the brains of middle-aged individuals that correlated with improved learning and memory after just 8 weeks of mindfulness training." },
            { type: "quote", text: "“Great performers never just hope they’ll be on top of their game. Rather they actively create specific conditions that will elicit their personal best.” — Brad Stulberg, Peak Performance" },
            { type: "p", text: "What's your daily routine? Successful athletes, musicians, and business leaders know the power of routines. Much of anxiety comes from uncertainty. Routines provide the brain with predictability, easing the stress of the unknown. Studies have shown that even a 10 to 15-minute warm-up routine improves focus, reduces mistakes, increases consistency, and lowers stress." },
            { type: "h3", text: "Leadership Activity:" },
            { type: "bullet", text: "Create a routine. Consistency is king. Steer away from doing hard mental work initially—choose an enjoyable task you're likely to repeat to build momentum." },
            { type: "bullet", text: "Physical Warm-Up: Go for a short walk, do light stretches, or practice physical actions you'll be doing." },
            { type: "bullet", text: "Mental Warm-Up: Mindfulness activity, organize your workspace, review your priority list, or journal." },
            { type: "bullet", text: "Environment Design: Stephen King noted, 'most of us do our best work in a place of our own.' Create a unique workspace. Distractions in your environment (like a messy desk) have a biological cost on your focus." }
        ]
    },
    {
        id: "mc_2",
        title: "Minimize Distraction",
        part: "Part 2",
        readTime: "3 min read",
        type: "Article",
        content: [
            { type: "p", text: "It isn't experience that sets top performers apart, but the amount of deliberate practice they put in. Practice doesn't make perfect. Perfect practice makes perfect." },
            { type: "p", text: "Anders Ericsson, a behavioral scientist, studied violinists at the Global Music Academy. He analyzed the habits of the top violinists compared to everyone else. Most individuals put in roughly the same amount of time each week. The difference was the quality of time. Elite violinists focused on specific goals, were intentional about being 'present', and fiercely minimized distractions." },
            { type: "p", text: "Mental flow states are periods in which our minds are intensely engaged in an activity. Distraction rips us out of this flow state. A study of college students published in the Journal of Social Psychology asked students to complete difficult tasks. The students who had their phones visible made significantly more mistakes even when their phones were turned off. Even having an empty black box shaped like a phone led to a higher number of errors. Our brains are highly tuned for distraction. The average person looks at their smartphone over 150 times each day." },
            { type: "h3", text: "The Multi-Tasking Myth:" },
            { type: "p", text: "Most everyone likes to multi-task because it is psychologically rewarding. Brain imaging studies show that when people are multi-tasking, the reward centers of the brain are much more active. The problem is that 99% of the population cannot effectively multi-task. Functional MRI studies show that multi-tasking leads to more errors and slower response times." },
            { type: "quote", text: "Try this: Count out loud from 1 to 25 as fast as you can. Now count from A to Z as fast as you can. Now switch back and forth (1-A, 2-B, 3-C). Guaranteed, your time will be much slower due to task-switching costs." },
            { type: "h3", text: "Leadership Activity:" },
            { type: "bullet", text: "Plan your day by taking time to first identify your goals. Be intentional about working on one task at a time." },
            { type: "bullet", text: "Identify your procrastination traps. (e.g., talking with people to avoid work, scrolling social media)." },
            { type: "bullet", text: "Design your barriers. Put the phone out of sight entirely, or schedule a specific window to respond to emails." }
        ]
    },
    {
        id: "mc_3",
        title: "Optimizing Focus & Attention",
        part: "Part 3",
        readTime: "4 min read",
        type: "Article",
        content: [
            { type: "p", text: "You've heard the phrase: 'Work smarter, not harder.' But what does that really mean in practice?" },
            { type: "p", text: "The average person works roughly 40 hours per week, with demanding positions hitting 60 to 80 hours. The average North American also spends about 4 hours per day looking at a screen in a non-work capacity. That's 1,440 hours each year. This means our brains have very little time away from intense cognitive input. A 2025 Forbes report indicated that two-thirds (66%) of employees are experiencing burnout, and 24% are stressed at work regularly." },
            { type: "p", text: "An international company called the Draugiem Group investigated the habits of their most successful workers using a sophisticated tracking app. What they found was that the top performers didn't work all the time. Rather, they developed a rhythm of working for approximately 50 minutes followed by a short break." },
            { type: "p", text: "The famed sleep researcher, Daniel Kleitman, found that humans naturally cycle between states of high and low alertness over 90 to 120 minutes. These daily 'ultradian' cycles dictate our optimal work-to-rest ratio. The principle of 'work hard, then take a break' is well established in high-level athletes, musicians, and writers." },
            { type: "quote", text: "A classic study by Galinski and colleagues found that mandatory 5-minute breaks in data entry workers led to decreased eye strain, fatigue, and muscle soreness with zero decrease in overall productivity." },
            { type: "h3", text: "Leadership Activity:" },
            { type: "bullet", text: "Divide your work into chunks of 50 to 90 minutes. It may take time to determine your optimal cycle." },
            { type: "bullet", text: "Plan your short break. It should include an activity that is NOT work related (go for a walk, practice mindfulness, listen to music)." },
            { type: "bullet", text: "Accountability: Who will help you reinforce this schedule?" }
        ]
    },
    {
        id: "mc_4",
        title: "Schedule Intense Activity",
        part: "Part 4",
        readTime: "3 min read",
        type: "Article",
        content: [
            { type: "p", text: "As much as we would like to believe we can work at high intensity for a sustained period, the reality is that we only have a finite amount of cognitive energy each day. Working with, not against, our natural biological capacity is the best pathway to maximizing execution." },
            { type: "p", text: "Mark Zuckerberg was famously asked why he wore the same grey t-shirt almost every day. He stated that in his role, he was making massive, high-stakes decisions. He wanted to minimize the number of trivial decisions he made every day to reduce cognitive load. This is why routines are so important—once established, they require almost zero cognitive energy to execute." },
            { type: "p", text: "Most people fall into two biological categories: Morning Larks and Night Owls. These are our natural daily rhythms governing when we have our most cognitively productive times for 'heavy mental lifting.' Some people enjoy getting up early when it's quiet to dive into abstract work. For others, this pattern is an arduous, painful experience, and they feel their highest alertness in the late afternoon." },
            { type: "h3", text: "Leadership Activity:" },
            { type: "bullet", text: "Reflect on your natural tendencies. Are you a morning person or a late-night person? Plan your daily calendar to align your most demanding tasks with your biological energy peaks." },
            { type: "bullet", text: "Audit your decisions. What trivial decisions are draining your cognitive battery? Build routines to automate them." },
            { type: "bullet", text: "When a large deadline is looming, explicitly schedule when you will tackle it, and protect that time block fiercely from external meetings." }
        ]
    },
    {
        id: "mc_5",
        title: "Purpose in Work",
        part: "Part 5",
        readTime: "5 min read",
        type: "Article",
        content: [
            { type: "p", text: "Consider the job duties of a hospital janitor. They sweep, mop, sanitize, and clean up biological messes. It's a daily grind. Most people would find it hard to motivate themselves to do this regularly. Yet, a University of Pennsylvania study of janitors discovered an interesting finding. The janitors who felt the most satisfaction didn't just think about 'cleaning'. They linked their work to a higher, 'self-transcendent' purpose: contributing to the care, safety, and healing of patients. Their motivation and job satisfaction were significantly higher." },
            { type: "p", text: "Victor Frankl, an Austrian neurologist who survived a WWII concentration camp, formulated the theory that humans have an innate vacuum to seek purpose. Even in the most horrific situations, individuals can find dignity and meaning." },
            { type: "p", text: "When faced with stress, our bodies naturally try to avoid pain. The amygdala acts as a literal threat detector. When activated, it triggers the Hypothalamus-Pituitary-Adrenal (HPA Axis) system to initiate escape behavior. But when we focus on something greater than ourselves, we can transcend our natural biological inclinations to quit." },
            { type: "quote", text: "Shalane Flanagan was the first American woman to win the NYC Marathon in 2017. When asked how she pushed through the pain to keep her lead, she stated she was thinking about running for other mothers, and for the victims of the 2014 Boston marathon bombing. Her purpose allowed her to override her biology." },
            { type: "p", text: "When you are doing something purposeful, your brain shifts. Dopamine—the neurochemical responsible for motivation and pursuit—is released when we believe 'we are on the right path.' People often experience massive mental breakthroughs when they realign their purpose with their daily tasks, much like the famous story of 'The Fred Factor' where a mail carrier turned an ordinary route into an extraordinary career." },
            { type: "h3", text: "Leadership Activity:" },
            { type: "bullet", text: "Why do you actually like your job? What is meaningful about the work you are doing?" },
            { type: "bullet", text: "Who are the people you care most about, and how does your work impact them?" },
            { type: "bullet", text: "What are the most difficult parts of your job? What new attitude or belief can you take into that situation tomorrow to reframe it with purpose?" }
        ]
    }
];

const LeadershipHubView = ({ onAskAi }) => {
    const [activeProtocol, setActiveProtocol] = useState('culture');
    const [activeTool, setActiveTool] = useState(null);
    
    // ACCORDION STATE: Tracks only one active model at a time
    const [expandedModel, setExpandedModel] = useState(null);

    const toggleModel = (idx) => {
        setExpandedModel(prev => prev === idx ? null : idx);
    };

    const leadershipData = {
        intro: {
            content: "True leadership is about setting the standard, fostering psychological safety, and driving high performance. Whether you are a team captain, a head coach, or a corporate executive, these core protocols build winning cultures and resilient teams."
        },
        protocols: {
            culture: {
                title: "Culture & Standards",
                description: "Culture isn't what you say; it's what you tolerate. Building a high-performance environment requires absolute clarity and alignment.",
                actions: [
                    { step: "Define", text: "Establish 3-5 non-negotiable core values. Keep them simple, memorable, and actionable." },
                    { step: "Model", text: "Leaders go first. You must visibly embody the standards and work ethic you expect from the team." },
                    { step: "Enforce", text: "Address breaches in standards immediately. Silence from leadership is perceived as permission." },
                    { step: "Reward", text: "Publicly recognize behaviors that align with the culture, not just the final outcomes or scoreboards." }
                ]
            },
            communication: {
                title: "Communication & Conflict",
                description: "Great teams don't lack conflict; they master it. Clear communication builds trust, drives accountability, and eliminates silos.",
                actions: [
                    { step: "Directness", text: "Clear is kind. Unclear is unkind. Deliver feedback directly without sandwiching it in fluff." },
                    { step: "Active Listening", text: "Listen to understand, not to reply. Ask 'What am I missing?' during disagreements." },
                    { step: "Psych Safety", text: "Reward people for bringing you bad news. If athletes or staff hide mistakes, the system breaks." },
                    { step: "Debrief", text: "Run blameless post-mortems after failures. Focus intensely on 'How did the process fail?' not 'Who failed?'" }
                ]
            },
            performance: {
                title: "High-Performance Mindset",
                description: "Leading through adversity requires emotional control, strategic focus, and the ability to pivot without panic.",
                actions: [
                    { step: "Regulate", text: "Your team mirrors your nervous system. Master your own emotional regulation before stepping in front of the group." },
                    { step: "Process Focus", text: "Detach from the outcome. Focus the team entirely on executing the next right action." },
                    { step: "Empower", text: "Push decision-making down to the lowest possible level. Build independent leaders, not dependent followers." },
                    { step: "Adaptability", text: "Embrace the pivot. When the game plan fails, shift to contingency planning immediately without complaining." }
                ]
            }
        },
        signs: [
            "Blame Shifting", "Fear of Failure", "Information Hoarding", 
            "Lack of Accountability", "Burnout as a Badge", "Artificial Harmony"
        ]
    };

    const getIconForType = (type) => {
        if (type === 'Video') return <Video size={20}/>;
        if (type === 'Link') return <LinkIcon size={20}/>;
        if (type === 'PDF') return <FileText size={20}/>;
        if (type === 'Article') return <BookOpen size={20}/>;
        return <Activity size={20}/>;
    };

    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const getPdfEmbedUrl = (url) => {
        if (!url) return '';
        if (url.includes('drive.google.com') && url.includes('/view')) {
            return url.replace('/view', '/preview');
        }
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    };

    // --- CUSTOM ARTICLE RENDERER ---
    const renderArticleContent = (contentArray) => {
        return (
            <div className="max-w-3xl mx-auto py-8 px-4 md:px-8 font-sans">
                {contentArray.map((block, idx) => {
                    if (block.type === 'h2') return <h2 key={idx} className="text-2xl font-black text-slate-800 mt-8 mb-4">{block.text}</h2>;
                    if (block.type === 'h3') return <h3 key={idx} className="text-xl font-bold text-slate-800 mt-8 mb-4">{block.text}</h3>;
                    if (block.type === 'quote') return (
                        <blockquote key={idx} className="border-l-4 border-eas-blue bg-blue-50/50 p-6 my-6 rounded-r-xl text-slate-700 italic font-medium leading-relaxed">
                            {block.text}
                        </blockquote>
                    );
                    if (block.type === 'bullet') return (
                        <div key={idx} className="flex gap-3 mb-4 items-start">
                            <div className="w-1.5 h-1.5 rounded-full bg-eas-blue mt-2 shrink-0"></div>
                            <p className="text-slate-700 leading-relaxed">{block.text}</p>
                        </div>
                    );
                    return <p key={idx} className="text-slate-700 leading-relaxed mb-5 text-lg">{block.text}</p>;
                })}
            </div>
        );
    };

    return (
        <div className="space-y-8 animate-in fade-in max-w-5xl mx-auto relative pb-10">
             
             {/* MODAL OVERLAY FOR ACTIVE TOOL OR ARTICLE */}
             {activeTool && (
                <div className="fixed inset-0 z-[200] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-2 md:p-4">
                    <div className="bg-white w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl flex flex-col h-[90vh] md:h-[85vh]">
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10 shrink-0">
                            <h3 className="font-bold text-sm md:text-lg text-slate-800 flex items-center gap-3 truncate pr-2">
                                <div className="p-2 bg-blue-100 text-eas-blue rounded-lg shrink-0">{getIconForType(activeTool.type)}</div>
                                <span className="truncate">{activeTool.title}</span>
                            </h3>
                            <button onClick={() => setActiveTool(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors shrink-0"><X size={20} className="text-slate-500"/></button>
                        </div>
                        <div className="flex-1 bg-white relative overflow-y-auto flex flex-col">
                            
                            {activeTool.type === 'Article' && (
                                <div className="w-full">
                                    <div className="bg-slate-50 border-b border-slate-100 py-10 px-6 text-center">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                                            {activeTool.part}
                                        </div>
                                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">{activeTool.title}</h1>
                                        <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-500">
                                            <Clock size={16} /> {activeTool.readTime}
                                        </div>
                                    </div>
                                    {renderArticleContent(activeTool.content)}
                                </div>
                            )}

                            {activeTool.type === 'Video' && (
                                <div className="w-full h-full bg-black flex items-center justify-center p-2 md:p-6 overflow-hidden">
                                    <div 
                                        className="w-full relative" 
                                        style={{ aspectRatio: '16/9', maxHeight: '100%', maxWidth: '1024px' }}
                                    >
                                        <CustomYouTubePlayer 
                                            videoId={getYouTubeVideoId(activeTool.url)} 
                                        />
                                    </div>
                                </div>
                            )}

                            {(activeTool.type === 'PDF' || activeTool.type === 'Link') && (
                                <div className="w-full h-full relative group">
                                    <iframe src={getPdfEmbedUrl(activeTool.url)} className="w-full h-full" frameBorder="0" title="PDF Viewer"></iframe>
                                    <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <a href={activeTool.url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg shadow-xl flex items-center gap-2 hover:bg-black transition-colors"><ExternalLink size={14}/> Open in New Tab</a>
                                    </div>
                                </div>
                            )}
                            {activeTool.type === 'Template' && (
                                <div className="overflow-y-auto h-full p-4 md:p-8 bg-slate-100">
                                    <pre className="whitespace-pre-wrap font-sans text-slate-600 text-sm md:text-base leading-relaxed p-4 md:p-8 bg-white rounded-xl border border-slate-200 shadow-sm max-w-3xl mx-auto">{activeTool.content}</pre>
                                </div>
                            )}
                        </div>
                        {activeTool.type === 'Template' && (
                            <div className="p-4 border-t border-slate-100 bg-white flex justify-end shrink-0 z-10">
                                <button onClick={() => {
                                    const blob = new Blob([activeTool.content], { type: "text/plain" });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a"); a.href = url; a.download = `${activeTool.title}.txt`; a.click();
                                }} className="px-5 py-2 bg-eas-blue text-white rounded-lg font-bold hover:bg-sky-600 transition-colors flex items-center gap-2 text-sm">
                                    <Download size={16}/> Download Text
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* HERO SECTION */}
            <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-3 py-1 bg-blue-50 text-eas-blue text-xs font-bold uppercase tracking-wider rounded-full">Executive & Coaching</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-black text-slate-900">Leadership Hub</h1>
                        </div>
                        <button 
                            onClick={() => onAskAi && onAskAi("Act as an executive coach. What is the most important leadership principle I should focus on today?")}
                            className="flex items-center justify-center w-full md:w-auto gap-3 px-5 py-2.5 bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 text-white rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-cyan-500/40 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 font-bold tracking-wide border border-white/20 group relative overflow-hidden shrink-0"
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-transparent translate-y-[-100%] group-hover:translate-y-[100%] transition-transform duration-700 ease-in-out"></div>
                            <MessageSquare size={24} className="fill-white/10 shrink-0" />
                            <span className="text-sm whitespace-nowrap">Ask AI Coach</span>
                            <Sparkles size={20} className="text-yellow-400 animate-pulse shrink-0" />
                        </button>
                    </div>
                    <p className="text-slate-600 text-lg leading-relaxed w-full max-w-3xl">
                        {leadershipData.intro.content}
                    </p>
                </div>
            </div>

            {/* --- NEW: EXECUTIVE MASTERCLASS SERIES --- */}
            <div>
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4 mt-8 px-1">
                    <div>
                        <h3 className="font-bold text-xl flex items-center gap-2 text-slate-800">
                            <BookOpen className="text-eas-blue" size={24} /> Executive Masterclass
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">Science-backed principles for peak workplace performance.</p>
                    </div>
                </div>
                
                {/* Responsive Grid for Course Modules */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {MASTERCLASS_ARTICLES.map((article, idx) => (
                        <div 
                            key={idx}
                            onClick={() => setActiveTool(article)}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:border-eas-blue hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer group flex flex-col h-48"
                        >
                            <div className="p-5 flex flex-col h-full">
                                <div className="flex justify-between items-start mb-auto">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-500 bg-blue-50 px-2 py-1 rounded-md">{article.part}</span>
                                    <div className="p-1.5 bg-slate-50 text-slate-400 rounded-lg group-hover:bg-blue-50 group-hover:text-eas-blue transition-colors">
                                        <BookOpen size={16} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-black text-lg text-slate-800 leading-tight mb-2 group-hover:text-eas-blue transition-colors line-clamp-2">{article.title}</h4>
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                                        <Clock size={12} /> {article.readTime}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SITUATIONAL AI ROLEPLAY */}
            <div>
                <h3 className="font-bold text-xl flex items-center gap-2 text-slate-800 px-1 mb-4 mt-4">
                    <Brain className="text-indigo-600" size={24} /> Situational AI Roleplay
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {COACHING_SCENARIOS.map((scenario, idx) => (
                        <button 
                            key={idx}
                            onClick={() => onAskAi && onAskAi(scenario.prompt)}
                            className={`p-5 rounded-2xl border transition-all text-left flex flex-col h-full group hover:shadow-lg hover:-translate-y-1 bg-white ${scenario.border}`}
                        >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${scenario.bg} group-hover:scale-110 transition-transform`}>
                                {scenario.icon}
                            </div>
                            <h4 className="font-bold text-slate-800 mb-2">{scenario.title}</h4>
                            <p className="text-xs text-slate-500 leading-relaxed flex-1">{scenario.desc}</p>
                            <div className="mt-4 text-[10px] font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                Start Scenario &rarr;
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* MENTAL MODELS & FRAMEWORKS (ACCORDION) */}
            <div>
                <h3 className="font-bold text-xl flex items-center gap-2 text-slate-800 px-1 mb-4 mt-8">
                    <Crosshair className="text-eas-blue" size={24} /> Mental Models
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                    {FRAMEWORKS.map((framework, idx) => {
                        const isExpanded = expandedModel === idx;
                        return (
                            <div 
                                key={idx} 
                                className={`bg-white rounded-2xl border transition-all flex flex-col ${isExpanded ? 'border-eas-blue shadow-md' : 'border-slate-200 shadow-sm hover:border-eas-blue hover:-translate-y-0.5'}`}
                            >
                                <div 
                                    onClick={() => toggleModel(idx)}
                                    className="p-6 cursor-pointer"
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className="font-black text-lg text-slate-800">{framework.title}</h4>
                                        <div className={`p-1 rounded-full transition-transform duration-300 ${isExpanded ? 'rotate-180 bg-blue-50 text-eas-blue' : 'text-slate-400'}`}>
                                            <ChevronDown size={20} />
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-2">{framework.desc}</p>
                                    
                                    {isExpanded && (
                                        <div 
                                            className="space-y-4 animate-in slide-in-from-top-2 duration-300 border-t border-slate-100 pt-5 mt-4 cursor-default"
                                            onClick={(e) => e.stopPropagation()} 
                                        >
                                            {framework.steps.map((step, sIdx) => (
                                                <div key={sIdx} className="flex gap-3 items-start">
                                                    <div className="w-6 h-6 rounded-full bg-blue-50 text-eas-blue flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                                                        {sIdx + 1}
                                                    </div>
                                                    <div>
                                                        <span className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-0.5">{step.label}</span>
                                                        <span className="block text-sm text-slate-600">{step.text}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* CORE PROTOCOLS */}
            <div>
                <h3 className="font-bold text-xl flex items-center gap-2 text-slate-800 px-1 mb-4 mt-8">
                    <Shield className="text-emerald-500" size={24} /> Core Protocols
                </h3>
                <div className="flex p-1 bg-slate-100/80 rounded-t-2xl border-b border-slate-200 gap-1 overflow-x-auto">
                    {Object.keys(leadershipData.protocols).map(key => (
                        <button 
                            key={key} 
                            onClick={() => setActiveProtocol(key)} 
                            className={`flex-1 py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm font-bold ${activeProtocol === key ? 'bg-white text-eas-blue shadow-sm ring-1 ring-black/5' : 'text-slate-500 hover:bg-white/60 hover:text-slate-700'}`}
                        >
                            {key === 'culture' && <Users size={18} />}
                            {key === 'communication' && <MessageSquare size={18} />}
                            {key === 'performance' && <Target size={18} />}
                            <span className="capitalize whitespace-nowrap">{key}</span>
                        </button>
                    ))}
                </div>
                <div className="bg-white rounded-b-2xl border border-slate-200 border-t-0 shadow-sm p-6 md:p-8">
                    {leadershipData.protocols[activeProtocol] && (
                        <div className="animate-in fade-in duration-300">
                            <div className="mb-6 pb-6 border-b border-slate-100">
                                <h3 className="text-2xl font-black text-slate-800 mb-2">{leadershipData.protocols[activeProtocol].title}</h3>
                                <p className="text-slate-500 text-base leading-relaxed">{leadershipData.protocols[activeProtocol].description}</p>
                            </div>
                            <div className="space-y-4">
                                {leadershipData.protocols[activeProtocol].actions.map((action, idx) => (
                                    <div key={idx} className="flex gap-5 p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:border-blue-200 hover:bg-blue-50/30 transition-all">
                                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 font-bold text-eas-blue text-sm shadow-sm mt-0.5">
                                            {idx + 1}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-slate-900 text-sm mb-1 uppercase tracking-wider">{action.step}</h4>
                                            <p className="text-slate-600 text-sm leading-relaxed">{action.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CULTURE KILLERS */}
            <div className="bg-red-50/50 p-8 rounded-3xl border border-red-100 shadow-sm mt-8">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="md:w-1/3">
                        <h3 className="font-black text-2xl flex items-center gap-2 mb-2 text-red-900">
                            <AlertCircle size={28} className="text-red-500" /> Culture Killers
                        </h3>
                        <p className="text-red-700/80 text-sm leading-relaxed">
                            Watch for these toxic traits. If they take root, psychological safety dies, and performance plummets. Silence from leadership is perceived as permission.
                        </p>
                    </div>
                    <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                        {leadershipData.signs.map((sign, idx) => (
                            <div key={idx} className="bg-white px-5 py-4 rounded-xl border border-red-100 flex items-center gap-3 text-sm shadow-sm">
                                <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]"></div>
                                <span className="font-bold text-slate-800">{sign}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default LeadershipHubView;